import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth } from "./firebaseConfig";
import { doc, getDoc, updateDoc, arrayUnion, Timestamp, increment } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import "./TakeQuiz.css";

const TakeQuiz = () => {
    const { quizId } = useParams();
    const [quiz, setQuiz] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const [answers, setAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [scoreData, setScoreData] = useState(null);
    const [allAnswered, setAllAnswered] = useState(false);
    const [currentFlashcard, setCurrentFlashcard] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        return onAuthStateChanged(auth, (currentUser) => {
            if (!currentUser) {
                navigate("/login");
            } else {
                setUser(currentUser);

            }
        });
    }, [navigate]);

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                const quizRef = doc(db, "quizzes", quizId);
                const quizSnap = await getDoc(quizRef);

                if (quizSnap.exists()) {
                    console.log("Fetched Quiz Data:", quizSnap.data());
                    console.log("Questions Array:", quizSnap.data().questions);
                    setQuiz(quizSnap.data());
                } else {
                    setError("Quiz not found!");
                }
            } catch (err) {
                setError("Failed to load quiz. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchQuiz();
    }, [quizId]);

    const handleSelectAnswer = (questionId, answerText) => {
        setAnswers((prevAnswers) => {
            const newAnswers = {
                ...prevAnswers,
                [questionId]: answerText.toString().toLowerCase(),
            };
            setAllAnswered(quiz.questions.every((q, index) => newAnswers[index] !== undefined));
            return newAnswers;
        });
    };

    const handleFlipCard = () => {
        setFlipped(!flipped);
    };

    const handleNextCard = () => {
        if (currentFlashcard < quiz.flashcards.length - 1) {
            setCurrentFlashcard(currentFlashcard + 1);
            setFlipped(false);
        }
    };

    const handlePrevCard = () => {
        if (currentFlashcard > 0) {
            setCurrentFlashcard(currentFlashcard - 1);
            setFlipped(false);
        }
    };

    const handleSubmitQuiz = async () => {
        if (!quiz || !user) return;
        if (isSubmitting) return;
        setIsSubmitting(true);

        let score = 0;

        quiz.questions.forEach((q, index) => {
            const selectedAnswer = answers[index];
            console.log(`Q${index + 1}: Selected Answer ->`, selectedAnswer);
            console.log(`Correct Answer ->`, q.answer);
            console.log(`Raw Question Data ->`, q);

            if (q.answer && typeof q.answer === "string") {
                if (selectedAnswer && selectedAnswer.toString().trim().toLowerCase() === q.answer.toString().trim().toLowerCase()) {
                    score += 1;
                }
            } else {
                console.error(`Error: Missing answer for question ${index + 1}`);
            }
        });

        const totalQuestions = quiz.questions.length;
        const scorePercentage = (score / totalQuestions) * 100;
        const passed = scorePercentage >= 70;

        const quizResult = {
            quizId,
            score,
            totalQuestions,
            scorePercentage,
            passed,
            Timestamp: Timestamp.now(),
            answers,
        };

        const quizRef = doc(db, "quizzes", quizId);

        await updateDoc(quizRef, {
            totalAttempts: increment(1),
            sumOfScores: increment(scorePercentage),
        }).then(async () => {
            const updatedSnap = await getDoc(quizRef);
            const updatedData = updatedSnap.data();

            const updatedTotal = updatedData.totalAttempts;
            const updatedSum = updatedData.sumOfScores;

            const newAvg = updatedTotal > 0 ? (updatedSum / updatedTotal).toFixed(2) : 0;

            await updateDoc(quizRef, { avgScore: parseFloat(newAvg) });
        });

        try {
            const userRef = doc(db, "users", user.uid);
            await updateDoc(userRef, {
                quizHistory: arrayUnion(quizResult),
            });

            setScoreData({ score, totalQuestions, scorePercentage, passed });
            setShowResults(true);
        } catch (err) {
            console.error("Error submitting quiz", err);
            alert("Failed to submit quiz. Please try again.");
        }
    };

    if (loading) return <p>Loading quiz...</p>;
    if (error) return <p>{error}</p>;

    return (
        <div className="take-quiz-container">
            <h2>{quiz?.title}</h2>

            {/* Flashcards */}
            {quiz?.quizType === "flashcards" && (quiz.flashcards.length > 0 ? (
                <div className="flashcard-container">
                    <div className="flashcard-wrapper">
                        <div className={`flashcard ${flipped ? "flipped" : ""}`} onClick={handleFlipCard}>
                            <div className="flashcard-front">{quiz.flashcards[currentFlashcard].front}</div>
                            <div className="flashcard-back">{quiz.flashcards[currentFlashcard].back}</div>
                        </div>
                        <div className="flashcard-controls">
                            <button onClick={handlePrevCard} disabled={currentFlashcard === 0}>
                                Previous
                            </button>
                            <button onClick={handleNextCard} disabled={currentFlashcard === quiz.flashcards.length - 1}>
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <p>No flashcards available for this quiz.</p>
            ))}

            {/* Standard Questions */}
            {quiz?.quizType === "standard" && quiz.questions.length > 0 && (

                <form>
                    <h3>Questions:</h3>
                    {quiz?.questions && quiz.questions.length > 0 ? (
                        quiz.questions.map((q, index) => (
                            <div key={index} className="question-block">
                                <p><strong>Q{index + 1}: </strong> {q.question || "Missing question"}</p>

                                {/* Multiple Choice Options */}
                                {q.type === "multiple-choice" && (
                                    q.options.map((option, i) => (
                                        <label className="option-label" key={i} >
                                            <input
                                                type="radio"
                                                name={`question-${index}`}
                                                value={option}
                                                onChange={() => handleSelectAnswer(index, option)}
                                                checked={answers[index] === option}
                                            />
                                            {option}
                                        </label>
                                    ))
                                )}

                                {/* True/False Options */}
                                {q.type === "true-false" && (
                                    <>
                                        <label className="option-label">
                                            <input
                                                type="radio"
                                                name={`question-${index}`}
                                                value="True"
                                                onChange={() => handleSelectAnswer(index, "True")}
                                                checked={answers[index]?.toString().toLowerCase() === "true"}

                                            />
                                            True
                                        </label>
                                        <label className="option-label" >
                                            <input
                                                type="radio"
                                                name={`question-${index}`}
                                                value="False"
                                                onChange={() => handleSelectAnswer(index, "False")}
                                                checked={answers[index]?.toString().toLowerCase() === "false"}

                                            />
                                            False
                                        </label>
                                    </>
                                )}

                                {/* Open-Ended Answer */}
                                {q.type === "open-ended" && (
                                    <input
                                        type="text"
                                        value={answers[index] || ""}
                                        onChange={(e) => handleSelectAnswer(index, e.target.value)}
                                    />
                                )}
                            </div>
                        ))
                    ) : (
                        <p>No questions available.</p>
                    )}
                    <button type="button" onClick={handleSubmitQuiz} disabled={!allAnswered || isSubmitting}> Submit Quiz </button>
                </form>
            )}

            {showResults && scoreData && (
                <>
                    <div className="modal" style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        padding: "20px",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                        textAlign: "center",
                    }}>
                        <div className="modal-content">
                            <h3>Results:</h3>
                            <p><strong>Score:</strong> {scoreData.score} / {scoreData.totalQuestions}</p>
                            <p><strong>Percentage:</strong> {scoreData.scorePercentage.toFixed(2)}%</p>
                            <p style={{ color: scoreData.passed ? "green" : "red" }}>
                                <strong> {scoreData.passed ? "Passed!" : "Failed!"} </strong>
                            </p>
                            <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
                        </div>
                    </div>
                </>
            )}

        </div>
    );
};

export default TakeQuiz;